import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Popconfirm,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePromotionPackages } from '../hooks';
import { PromotionPackage, PromotionLevel } from '../../api/services/adminService';

const { Title, Text } = Typography;

const LEVEL_COLORS: Record<PromotionLevel, string> = {
  STANDARD: 'default',
  FEATURED: 'blue',
  PREMIUM: 'purple',
  TOP: 'gold',
  WEEK: 'orange',
  MONTH: 'red',
};

const PROMOTION_LEVELS: PromotionLevel[] = [
  'STANDARD',
  'FEATURED',
  'PREMIUM',
  'TOP',
  'WEEK',
  'MONTH',
];

const PromotionPackages: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    packages,
    loading,
    modalVisible,
    editingPackage,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch,
  } = usePromotionPackages();

  useEffect(() => {
    if (modalVisible && editingPackage) {
      form.setFieldsValue({
        name: editingPackage.name,
        promotionLevel: editingPackage.promotionLevel,
        creditCost: editingPackage.creditCost,
        durationDays: editingPackage.durationDays,
        sortOrder: editingPackage.sortOrder,
        isActive: editingPackage.isActive,
      });
    } else if (modalVisible && !editingPackage) {
      form.resetFields();
      form.setFieldsValue({ isActive: true, sortOrder: 0, promotionLevel: 'STANDARD' });
    }
  }, [modalVisible, editingPackage, form]);

  const onFinish = (values: any) => {
    if (editingPackage) {
      handleUpdate(editingPackage.id, values);
    } else {
      handleCreate(values);
    }
  };

  const columns = [
    {
      title: t('admin.promotion_packages.col_name'),
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => (
        <Space>
          <StarOutlined style={{ color: 'var(--admin-primary)' }} />
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.promotion_packages.col_level'),
      dataIndex: 'promotionLevel',
      key: 'promotionLevel',
      render: (v: PromotionLevel) => (
        <Tag color={LEVEL_COLORS[v]} style={{ fontWeight: 600 }}>
          {v}
        </Tag>
      ),
    },
    {
      title: t('admin.promotion_packages.col_credit_cost'),
      dataIndex: 'creditCost',
      key: 'creditCost',
      render: (v: number) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {t('admin.promotion_packages.col_credits_unit', { count: v })}
        </Tag>
      ),
    },
    {
      title: t('admin.promotion_packages.col_duration'),
      dataIndex: 'durationDays',
      key: 'durationDays',
      render: (v: number) => <Text>{t('admin.promotion_packages.col_duration_days', { count: v })}</Text>,
    },
    {
      title: t('admin.promotion_packages.col_sort_order'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t('admin.promotion_packages.col_status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? t('admin.promotion_packages.status_active') : t('admin.promotion_packages.status_inactive')}</Tag>
      ),
    },
    {
      title: t('admin.promotion_packages.col_actions'),
      key: 'actions',
      render: (_: any, record: PromotionPackage) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            style={{ color: 'var(--admin-primary)' }}
          />
          <Popconfirm
            title={t('admin.promotion_packages.delete_confirm')}
            description={t('admin.promotion_packages.delete_desc')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.promotion_packages.btn_delete')}
            okType="danger"
            cancelText={t('admin.promotion_packages.btn_cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{t('admin.promotion_packages.title')}</Title>
          <Text type="secondary">{t('admin.promotion_packages.subtitle')}</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading} className="admin-filter-btn">
            {t('admin.promotion_packages.btn_refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="ant-btn-primary admin-filter-btn admin-filter-btn-primary"
          >
            {t('admin.promotion_packages.btn_add')}
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Card bordered={false} className="admin-table-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={packages}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('admin.promotion_packages.empty_text') }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <Space>
            <StarOutlined style={{ color: 'var(--admin-primary)' }} />
            {editingPackage ? t('admin.promotion_packages.modal_edit_title') : t('admin.promotion_packages.modal_add_title')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        okText={editingPackage ? t('admin.promotion_packages.modal_save') : t('admin.promotion_packages.modal_create')}
        okButtonProps={{ className: 'ant-btn-primary' }}
        cancelText={t('admin.promotion_packages.btn_cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t('admin.promotion_packages.label_name')}
            rules={[{ required: true, message: t('admin.promotion_packages.validation_name') }]}
          >
            <Input placeholder={t('admin.promotion_packages.ph_name')} />
          </Form.Item>
          <Form.Item
            name="promotionLevel"
            label={t('admin.promotion_packages.label_level')}
            rules={[{ required: true, message: t('admin.promotion_packages.validation_level') }]}
          >
            <Select placeholder={t('admin.promotion_packages.ph_select_level')}>
              {PROMOTION_LEVELS.map(level => (
                <Select.Option key={level} value={level}>
                  <Tag color={LEVEL_COLORS[level]}>{level}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="creditCost"
            label={t('admin.promotion_packages.label_credit_cost')}
            rules={[{ required: true, message: t('admin.promotion_packages.validation_credit_cost') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder={t('admin.promotion_packages.ph_credit_cost')} />
          </Form.Item>
          <Form.Item
            name="durationDays"
            label={t('admin.promotion_packages.label_duration')}
            rules={[{ required: true, message: t('admin.promotion_packages.validation_duration') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder={t('admin.promotion_packages.ph_duration')} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('admin.promotion_packages.label_sort_order')}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
          <Form.Item name="isActive" label={t('admin.promotion_packages.label_active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PromotionPackages;
